import { createElement, useCallback, useLayoutEffect, useMemo, useRef, useState, type ComponentType, type ChangeEvent, type KeyboardEvent } from 'react'
import { Button, Input, InputNumber, Select, Slider, Switch, Typography } from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import type { FieldControl, FieldControlEdit } from '../config/field-controls'

type Props<C extends FieldControl = FieldControl> = {
  label: string; control: C; disabled: boolean; onEdit: (edit: FieldControlEdit) => void
}
type ControlProps<K extends FieldControl['kind']> = Props<Extract<FieldControl, { kind: K }>>
const ROWS = { minRows: 1, maxRows: 5 }
const ADD_ICON = <PlusOutlined aria-hidden />
const DELETE_ICON = <DeleteOutlined aria-hidden />

const TextValue = ({ label, control, disabled, onEdit }: ControlProps<'text'>) => {
  const change = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => onEdit({ kind: 'replace', value: event.target.value }), [onEdit])
  return <Input.TextArea aria-label={label} value={control.value} autoSize={ROWS} onChange={change} disabled={disabled} />
}
const NumberValue = ({ label, control, disabled, onEdit }: ControlProps<'number'>) => {
  const change = useCallback((value: number | null) => {
    if (value !== null && Number.isFinite(value)) onEdit({ kind: 'replace', value })
  }, [onEdit])
  return <InputNumber aria-label={label} value={control.value} onChange={change} disabled={disabled} />
}
const BooleanValue = ({ label, control, disabled, onEdit }: ControlProps<'boolean'>) => {
  const change = useCallback((value: boolean) => onEdit({ kind: 'replace', value }), [onEdit])
  return <Switch aria-label={label} checked={control.value} onChange={change} disabled={disabled} checkedChildren="开启" unCheckedChildren="关闭" />
}
const ChoiceValue = ({ label, control, disabled, onEdit }: ControlProps<'choice'>) => {
  const change = useCallback((value: string) => onEdit({ kind: 'replace', value }), [onEdit])
  return <Select aria-label={label} value={control.value} options={control.options} onChange={change} disabled={disabled} />
}
const ModulesValue = ({ label, control, disabled, onEdit }: ControlProps<'modules'>) => {
  const change = useCallback((value: string[]) => onEdit({ kind: 'modules', value }), [onEdit])
  return <Select mode="multiple" aria-label={label} value={control.value} options={control.options}
    onChange={change} disabled={disabled} optionFilterProp="label" />
}

const SliderValue = ({ label, control, disabled, onEdit }: ControlProps<'slider'>) => {
  const [preview, setPreview] = useState<{ control: typeof control; position: number } | null>(null)
  const gesture = useRef<{ control: typeof control; onEdit: typeof onEdit; cancelled?: boolean } | null>(null)
  const marks = useMemo(() => Object.fromEntries(control.options.map((option, index) => [index, option.label])), [control.options])
  const tooltip = useMemo(() => ({ formatter: (position?: number) => control.options[position ?? control.position]?.label }), [control])
  const valueText = useCallback((position: number) => control.options[position]?.label ?? '', [control])
  const change = useCallback((position: number) => {
    if (disabled) return
    gesture.current ??= { control, onEdit }
    if (gesture.current.control !== control || gesture.current.cancelled) return
    setPreview({ control, position })
  }, [control, disabled, onEdit])
  const complete = useCallback((position: number) => {
    const pending = gesture.current
    gesture.current = null; setPreview(null)
    if (!pending || pending.cancelled || pending.control !== control || disabled || !Number.isInteger(position)) return
    const value = control.options[position]?.value
    if (value !== undefined && value !== control.value) pending.onEdit({ kind: 'replace', value })
  }, [control, disabled])
  const cancel = useCallback(() => {
    if (gesture.current) gesture.current.cancelled = true
    setPreview(null)
  }, [])
  // Slider blurs its handle internally during rail clicks; only an explicit new gesture resets cancellation.
  const start = useCallback(() => { gesture.current = null }, [])
  const keyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (['Escape', 'Tab'].includes(event.key)) { cancel(); return }
    if (!event.repeat && gesture.current?.cancelled) gesture.current = null
  }, [cancel])
  useLayoutEffect(() => () => { gesture.current = null }, [])
  const position = preview?.control === control && !disabled ? preview.position : control.position
  return <div className="instance-administration__field-slider" onKeyDown={keyDown} onPointerCancel={cancel}
    onMouseDownCapture={start} onTouchStartCapture={start}>
    <Slider ariaLabelForHandle={label} ariaValueTextFormatterForHandle={valueText} min={0} max={control.options.length - 1}
      step={null} marks={marks} value={position} onChange={change} onChangeComplete={complete} disabled={disabled} tooltip={tooltip} />
  </div>
}

const ListItem = ({ label, index, value, disabled, onEdit }: Pick<Props, 'label' | 'disabled' | 'onEdit'> & { index: number; value: string }) => {
  const change = useCallback((event: ChangeEvent<HTMLInputElement>) => onEdit({ kind: 'list-item', index, value: event.target.value }), [index, onEdit])
  const remove = useCallback(() => onEdit({ kind: 'remove', index }), [index, onEdit])
  return <div className="instance-administration__field-list-item">
    <Input aria-label={`${label}第${index + 1}条`} value={value} onChange={change} disabled={disabled} />
    <Button aria-label={`删除${label}第${index + 1}条`} icon={DELETE_ICON} onClick={remove} disabled={disabled} />
  </div>
}
const ListValue = ({ label, control, disabled, onEdit }: ControlProps<'list'>) => {
  const add = useCallback(() => onEdit({ kind: 'append' }), [onEdit])
  return <div className="instance-administration__field-list">
    {control.value.map((value, index) => <ListItem key={index} label={label} index={index} value={value} disabled={disabled} onEdit={onEdit} />)}
    <Button aria-label={`新增${label}`} icon={ADD_ICON} onClick={add} disabled={disabled}>新增条目</Button>
  </div>
}
const ReadonlyValue = ({ control }: ControlProps<'readonly'>) => {
  const value = control.value
  const text = value !== null && typeof value === 'object' ? JSON.stringify(value) : String(value)
  return <Typography.Text type="secondary">{text}</Typography.Text>
}

const CONTROLS = { text: TextValue, number: NumberValue, boolean: BooleanValue, choice: ChoiceValue,
  slider: SliderValue, modules: ModulesValue, list: ListValue, readonly: ReadonlyValue }

/** The normalized tag selects a renderer; configuration paths and stored-value policies stay upstream. */
export const FieldValue = (props: Props) => <div className="instance-administration__field-value">
  {createElement(CONTROLS[props.control.kind] as ComponentType<Props>, props)}
</div>
